import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { AuthContextPublic } from '../context/AuthContextPublic';
import { Col, Container, Row } from 'react-bootstrap';
import { Toaster, toast } from 'react-hot-toast';
import { firestore, functions, auth } from '../Firebase';
import { serverTimestamp } from 'firebase/firestore';
import HelpModal, { parseHelperItems } from './HelpModal';


const getClientDeviceInfo = () => {
  const userAgent = navigator.userAgent || '';
  const userAgentData = navigator.userAgentData;

  const browser = (() => {
    const brands = userAgentData?.brands || [];
    const preferredBrand = brands.find(
      (brand) => !/Chromium|Not A\(?Brand/i.test(brand.brand)
    );

    if (preferredBrand?.brand) {
      return `${preferredBrand.brand}${preferredBrand.version ? ` ${preferredBrand.version}` : ''}`;
    }
    if (/Edg\//.test(userAgent)) return `Edge ${userAgent.match(/Edg\/([\d.]+)/)?.[1] || ''}`.trim();
    if (/Chrome\//.test(userAgent)) return `Chrome ${userAgent.match(/Chrome\/([\d.]+)/)?.[1] || ''}`.trim();
    if (/Firefox\//.test(userAgent)) return `Firefox ${userAgent.match(/Firefox\/([\d.]+)/)?.[1] || ''}`.trim();
    if (/Version\/.*Safari\//.test(userAgent)) return `Safari ${userAgent.match(/Version\/([\d.]+)/)?.[1] || ''}`.trim();
    return 'Unknown';
  })();

  const os = (() => {
    const platform = userAgentData?.platform || navigator.platform || '';
    if (/Windows/i.test(platform) || /Windows NT/i.test(userAgent)) return 'Windows';
    if (/macOS|Mac/i.test(platform) || /Mac OS X/i.test(userAgent)) return 'macOS';
    if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
    if (/Android/i.test(userAgent)) return 'Android';
    if (/Linux/i.test(platform) || /Linux/i.test(userAgent)) return 'Linux';
    return 'Unknown';
  })();

  const deviceType = (() => {
    if (userAgentData?.mobile || /Mobi|Android|iPhone|iPod/i.test(userAgent)) return 'mobile';
    if (/iPad|Tablet/i.test(userAgent)) return 'tablet';
    return 'desktop';
  })();

  return { browser, os, deviceType };
};

// Main component for public login
const LoginComponentPublic = () => {
  // Destructure values and functions from AuthContextPublic
  const {
    publicLogin,
    registerUser,
    checkUserExists,
    loginRegisteredUser,
    verifyPasscode,
    error,
    clearError,
  } = useContext(AuthContextPublic);

  // Original form input states
  const [passcode, setPasscode] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [userName, setUserName] = useState('');
  const [errors, setErrors] = useState({
    userName: false,
    passcode: false,
  });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [location, setLocation] = useState('');
  const [email, setEmail] = useState('');

  // Cookie notification state
  const [showCookieNotification, setShowCookieNotification] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Consolidated feature flags
  const [features, setFeatures] = useState({
    hasCookieNotification: false,
    hasEventPasscode: false,
    hasFirstLastNames: false,
    hasEmailField: false,
    hasLocationField: false,
    hasBackgroundImage: false,
    hasLightSubtitle: false,
    hasLoginLogo: false,
    hasDarkModeOn: false,
    has300pxLogo: false,
    hasRegistration: false,
    hasRegistrationCode: false,
    hasPreRegistration: false,
    hasLoginSubtitle: false,
    hasLoginLowerImage: false,
    hasLoginUniqueBGHex: false,
    hasOneTimeLogin: false,
    hasSessionChecker: false,
    hasConferenceModeOn: false,
  });

  // Consolidated customization values
  const [customization, setCustomization] = useState({
    subtitle: '',
    loginButtonMessage: 'Sign in',
    logoPath: '',
    bgPath: '',
    hex1: '',
    hex2: '',
    lowerImagePath: '',
    accessMessage: '',
    loginHex1: '',
    loginHex2: '',
    helperItems: '',
  });

  // Refs for input fields
  const userNameInputRef = useRef(null);
  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);
  const passcodeInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const emailInputRef = useRef(null);

  // Single useEffect for features collection
  useEffect(() => {
    const unsubscribe = firestore
      .collection('settings')
      .doc('features')
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            setFeatures({
              hasCookieNotification: data.hasCookieNotification || false,
              hasEventPasscode: data.hasEventPasscode || false,
              hasFirstLastNames: data.hasFirstLastNames || false,
              hasEmailField: data.hasEmailField || false,
              hasLocationField: data.hasLocationField || false,
              hasBackgroundImage: data.hasBackgroundImage || false,
              hasLightSubtitle: data.hasLightSubtitle || false,
              hasLoginLogo: data.hasLoginLogo || false,
              hasDarkModeOn: data.hasDarkModeOn || false,
              has300pxLogo: data.has300pxLogo || false,
              hasRegistration: data.hasRegistration || false,
              hasRegistrationCode: data.hasRegistrationCode || false,
              hasPreRegistration: data.hasPreRegistration || false,
              hasLoginSubtitle: data.hasLoginSubtitle || false,
              hasLoginLowerImage: data.hasLoginLowerImage || false,
              hasLoginUniqueBGHex: data.hasLoginUniqueBGHex || false,
              hasOneTimeLogin: data.hasOneTimeLogin || false,
              hasSessionChecker: data.hasSessionChecker || false,
              hasConferenceModeOn: data.hasConferenceModeOn || false,
            });
          }
        },
        (error) => {
          console.error('Error listening to features changes:', error);
        }
      );

    return () => unsubscribe();
  }, []);

  // Single useEffect for customization collection
  useEffect(() => {
    const unsubscribe = firestore
      .collection('settings')
      .doc('customization')
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            setCustomization({
              subtitle: data.subtitle || '',
              loginButtonMessage: data.loginButtonMessage || 'Sign in',
              logoPath: data.logoPath || '',
              bgPath: data.bgPath || '',
              hex1: data.hex1 || '',
              hex2: data.hex2 || '',
              lowerImagePath: data.lowerImagePath || '',
              accessMessage: data.accessMessage || '',
              loginHex1: data.loginHex1 || '',
              loginHex2: data.loginHex2 || '',
              helperItems: data.helperItems || '',
            });
          }
        },
        (error) => {
          console.error('Error listening to customization changes:', error);
        }
      );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Focus on the appropriate input based on configuration
    if (features.hasRegistration && !isRegistering) {
      emailInputRef.current?.focus();
    } else if (features.hasFirstLastNames) {
      firstNameInputRef.current?.focus();
    } else {
      userNameInputRef.current?.focus();
    }
  }, [features.hasFirstLastNames, features.hasRegistration, isRegistering]);

  useEffect(() => {
    if (error && passcodeInputRef.current) {
      toast.error(error);
      setErrors((prevErrors) => ({ ...prevErrors, passcode: true }));
      passcodeInputRef.current.focus();
    }
  }, [error]);

  // Update the userName when firstName or lastName changes
  useEffect(() => {
    if (features.hasFirstLastNames) {
      setUserName(`${firstName} ${lastName}`.trim());
    }
  }, [firstName, lastName, features.hasFirstLastNames]);



  // Function to add viewer session data to Firestore
  const addViewerToFirestore = useCallback(async (additionalData = {}) => {
    // Capture additional analytics
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const referrer = document.referrer || 'Direct';
    // Network Information API is experimental/Chrome-only
    const connectionType = navigator.connection 
      ? navigator.connection.effectiveType 
      : 'unknown';
    const clientDeviceInfo = getClientDeviceInfo();

    // Prepare viewer data object
    const viewerData = {
      userName: features.hasFirstLastNames
        ? `${firstName} ${lastName}`.trim()
        : userName,
      firstName: firstName,
      lastName: lastName,
      location: location,
      email: email,
      screenResolution,
      referrer,
      connectionType,
      ...clientDeviceInfo,
      ...additionalData,
      // Browser/OS/Device will be parsed by Cloud Function when available.
      // Client values are sent as a fallback so session logs still have data
      // if the callable function fails.
    };

    try {
      // Use the userID from auth context
      // When registered, this userID is the user's document ID from the 'users' collection
      // When anonymous, this userID is the Firebase Auth UID
      const userID = localStorage.getItem('userID');

      // Ensure we are authenticated before calling the function
      if (!auth.currentUser) {
        console.warn('addViewerToFirestore: User not authenticated. Skipping.');
        return;
      }

      if (userID) {
        // If we have a registrationID, use that as the primary ID if available, 
        // otherwise fallback to auth UID (which is userID)
        // Actually, let's keep userID as the Auth UID for consistency with Firebase Auth
        // and just ensure registrationID is passed in viewerData.
        
        // However, user requested: "userID should actually be the ID of the users doc ID that it's associated with"
        // If we change userID here, we might break auth context assumptions.
        // Let's check where userID comes from. It comes from AuthContextPublic state/localStorage.
        
        // If we are registered, we set userID to registrationID in handleLogin context update?
        // Let's verify handleLogin logic.
        
        // Wait, if we change the ID of the document in 'viewers' collection to be the registrationID,
        // multiple sessions from the same registered user will overwrite each other.
        // That might be intended if "hasSessionChecker" implies one session at a time.
        // But LoginSessions table header says "Session ID".
        
        // User said: "when a viewer logs in and starts a viewer session, we should call that sessionID (which is interestingly what the table header is in the Login Sessions table)"
        
        // So:
        // 1. The document ID in 'viewers' collection should be the sessionID (Firebase Auth UID / Anonymous ID).
        // 2. We should add a field 'userID' to the viewer document which holds the registered user's doc ID.
        // 3. We should add a field 'sessionID' to the viewer document which holds the Firebase Auth UID.
        
        const sessionID = auth.currentUser.uid; // This is the unique session ID from anonymous auth
        
        const finalViewerData = {
            ...viewerData,
            sessionID: sessionID,
            userID: viewerData.registrationID || '' // User ID is blank if not registered
        };

        // Call Cloud Function to handle login and IP capture securely
        const loginUser = functions.httpsCallable('loginUser');
        await loginUser({ 
          userID: sessionID, // We use sessionID as the document key in viewers to allow multiple sessions if needed? 
                             // OR user wants to overwrite? 
                             // "The document ID in viewers collection... userID should actually be the ID of the users doc ID"
                             // If we use user doc ID as key, it enforces single session per user effectively.
                             // But wait, "when a viewer logs in... we should call that sessionID"
                             
                             // Let's stick to using the Auth UID as the document Key (Session ID)
                             // And add a field 'userID' inside the document.
                             
          userData: finalViewerData,
          userAgent: navigator.userAgent 
        });
      }
    } catch (error) {
      console.error('Error adding viewer data: ', error);
      // Fallback: Write directly to Firestore
      try {
        // Fallback logic mirroring above
        const sessionID = auth.currentUser?.uid;
        if (sessionID) {
           const finalViewerData = {
                ...viewerData,
                sessionID: sessionID,
                userID: viewerData.registrationID || ''
           };
           
           await firestore.collection('viewers').doc(sessionID).set({
             ...finalViewerData,
             isAdmin: false,
             type: 'active',
             IP: 'Unavailable (Fallback)',
             browser: clientDeviceInfo.browser,
             os: clientDeviceInfo.os,
             deviceType: clientDeviceInfo.deviceType,
             screenResolution,
             referrer,
             connectionType,
             loginTime: serverTimestamp(),
             timestamp: serverTimestamp(),
             lastUpdated: new Date().getTime(),
             sessionID: sessionID // Explicitly add sessionID for fallback
           }, { merge: true });
        }
      } catch (fbError) {
        console.error("Fallback failed", fbError);
      }
    }
  }, [
    userName,
    firstName,
    lastName,
    location,
    email,
    features.hasFirstLastNames,
  ]);

  // Function to validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Function to handle login form submission
  const handleLogin = async (event) => {
    event.preventDefault();
    clearError();

    // Add email validation
    if (features.hasEmailField && (!email || !isValidEmail(email))) {
      toast.error('Please enter a valid email address');
      emailInputRef.current?.focus();
      return;
    }

    if (features.hasRegistration) {
      // REGISTRATION MODE LOGIC
      if (isRegistering && !features.hasPreRegistration) {
        // Validation for Registration
        if (!email || !isValidEmail(email)) {
          toast.error('Please enter a valid email address');
          emailInputRef.current?.focus();
          return;
        }
        if (features.hasFirstLastNames && (!firstName || !lastName)) {
          toast.error('Please enter your first and last name');
          firstNameInputRef.current?.focus();
          return;
        }
        if (!features.hasFirstLastNames && !userName) {
          toast.error('Please enter your name');
          userNameInputRef.current?.focus();
          return;
        }
        if (features.hasLocationField && !location) {
          toast.error('Please enter your location');
          locationInputRef.current?.focus();
          return;
        }
        // Check Event Passcode if required for registration
        if (features.hasRegistrationCode) {
          if (!passcode) {
            toast.error('Please enter the registration code');
            passcodeInputRef.current?.focus();
            return;
          }
          // We force the check because registration MUST have the passcode correct
          // even if hasEventPasscode is false but hasRegistrationCode is true
          if (!verifyPasscode(passcode, true)) {
            toast.error('Incorrect registration code');
            passcodeInputRef.current?.focus();
            return;
          }
        }

        try {
          const userData = {
            firstName: firstName || '',
            lastName: lastName || '',
            userName: features.hasFirstLastNames
              ? `${firstName} ${lastName}`.trim()
              : userName,
            location: location || '',
            email: email,
            role: 'viewer', // Default role
            loggedIn: false,
          };
          
          // Check if user already exists before registering?
          // For simplicity, we assume uniqueness on email or just create new.
          // Better to check first.
          const check = await checkUserExists(email);
          if (check.exists) {
              toast.error('Email already registered. Please sign in.');
              setIsRegistering(false);
              return;
          }

          const docRef = await registerUser(userData);
          const registrationID = docRef.id;
          
          // After registration, log them in anonymously
          await loginRegisteredUser(userData);

          if (features.hasOneTimeLogin) {
            await docRef.update({ loggedIn: true });
          }

          // And add their session data
          await addViewerToFirestore({ ...userData, registrationID });
        } catch (err) {
          console.error(err);
        }

      } else {
        // LOGIN MODE LOGIC (Sign In with Email)
        if (!email || !isValidEmail(email)) {
          toast.error('Please enter a valid email address');
          emailInputRef.current?.focus();
          return;
        }

        try {
          // Check against "users" collection first
          const result = await checkUserExists(email);

          if (!result.exists) {
            if (features.hasPreRegistration) {
              toast.error(
                'Email not registered. Registration is closed or by invitation only.'
              );
            } else {
              toast.error('Email not registered. Please register.');
              setIsRegistering(true); // Switch to registration form
            }
            return;
          }

          if (features.hasOneTimeLogin && result.user.loggedIn) {
            toast.error('This user has already logged in. Access denied.');
            return;
          }

          if (features.hasSessionChecker) {
            try {
              const activeSessionsQuery = await firestore
                .collection('viewers')
                .where('userID', '==', result.ref.id)
                .get();

              const cutoffTime = new Date().getTime() - 90000; // 90 seconds
              const hasActiveSession = activeSessionsQuery.docs.some((doc) => {
                const data = doc.data();
                let lastUpdated = 0;
                if (typeof data.lastUpdated === 'number') {
                  lastUpdated = data.lastUpdated;
                } else if (data.lastUpdated && data.lastUpdated.seconds) {
                  lastUpdated = data.lastUpdated.seconds * 1000;
                }
                return lastUpdated > cutoffTime;
              });

              if (hasActiveSession) {
                toast.error(
                  'You are already logged in on another device. Only one session is allowed.'
                );
                return;
              }
            } catch (error) {
              console.error('Error checking active sessions:', error);
              // Optionally handle error, e.g., allow login if check fails
            }
          }

          // If valid, proceed to anonymous login
          // We use the name from the registered user data
          // We bypass passcode check because they are already registered

          await loginRegisteredUser(result.user);

          if (features.hasOneTimeLogin) {
            await result.ref.update({ loggedIn: true });
          }

          await addViewerToFirestore({
            ...result.user,
            registrationID: result.ref.id,
          });
        } catch (err) {
            console.error(err);
        }
      }
      return;
    }

    // ORIGINAL LOGIC (Non-Registration Mode)
    if (
      features.hasFirstLastNames &&
      (!firstName || !lastName) &&
      features.hasEventPasscode &&
      !passcode &&
      features.hasLocationField &&
      !location &&
      features.hasEmailField &&
      !email
    ) {
      toast.error(
        'Please enter your first name, last name, passcode, location, and email'
      );
      firstNameInputRef.current?.focus();
    } else if (
      features.hasFirstLastNames &&
      (!firstName || !lastName) &&
      features.hasLocationField &&
      !location
    ) {
      toast.error('Please enter your first name, last name, and location');
      firstNameInputRef.current?.focus();
    } else if (features.hasFirstLastNames && (!firstName || !lastName)) {
      toast.error('Please enter your first and last name');
      firstNameInputRef.current?.focus();
    } else if (
      !userName &&
      features.hasEventPasscode &&
      !passcode &&
      features.hasLocationField &&
      !location
    ) {
      toast.error('Please enter your name, passcode, and location');
      userNameInputRef.current?.focus();
    } else if (!userName && features.hasLocationField && !location) {
      toast.error('Please enter your name and location');
      userNameInputRef.current?.focus();
    } else if (!userName) {
      toast.error('Please enter your name');
      userNameInputRef.current?.focus();
    } else if (
      features.hasEventPasscode &&
      !passcode &&
      features.hasLocationField &&
      !location
    ) {
      toast.error('Please enter the passcode and location');
      passcodeInputRef.current?.focus();
    } else if (features.hasEventPasscode && !passcode) {
      toast.error('Please enter the passcode');
      passcodeInputRef.current?.focus();
    } else if (features.hasLocationField && !location) {
      toast.error('Please enter your location');
      locationInputRef.current?.focus();
    } else {
      try {
        await publicLogin(userName, passcode);
        addViewerToFirestore();
      } catch (err) {
        // Error handled in publicLogin or here
        if (error) {
            toast.error(error);
            setErrors((prevErrors) => ({ ...prevErrors, passcode: true }));
            passcodeInputRef.current?.focus();
        }
      }
    }
  };

  // Function to handle username input change
  const handleUserNameChange = (e) => {
    setUserName(e.target.value);
    if (e.target.value) {
      setErrors((prevErrors) => ({ ...prevErrors, userName: false }));
    }
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
    if (e.target.value) {
      setErrors((prevErrors) => ({ ...prevErrors, firstName: false }));
    }
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
    if (e.target.value) {
      setErrors((prevErrors) => ({ ...prevErrors, lastName: false }));
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && isValidEmail(value)) {
      setErrors((prevErrors) => ({ ...prevErrors, email: false }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, email: true }));
    }
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    if (e.target.value) {
      setErrors((prevErrors) => ({ ...prevErrors, location: false }));
    }
  };

  const handlePasscodeChange = (e) => {
    setPasscode(e.target.value);
    if (e.target.value) {
      setErrors((prevErrors) => ({ ...prevErrors, passcode: false }));
    }
  };

  const handleAcceptCookies = () => {
    setShowCookieNotification(false);
    localStorage.setItem('cookiesAccepted', 'true');
  };

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    if (cookiesAccepted !== 'true') {
      setTimeout(() => {
        setShowCookieNotification(true);
      }, 100);
    }
  }, []);

  // Ensure hex values have # prefix if they don't already
  const getHex = (hex, defaultHex) => {
    if (!hex) return defaultHex;
    return hex.startsWith('#') ? hex : `#${hex}`;
  };

  const hex1 = features.hasLoginUniqueBGHex
    ? getHex(customization.loginHex1, '#ffffff')
    : getHex(customization.hex1, '#ffffff');

  const hex2 = features.hasLoginUniqueBGHex
    ? getHex(customization.loginHex2, '#ffffff')
    : getHex(customization.hex2, '#ffffff');

  // Add custom CSS for dark mode floating labels
  useEffect(() => {
    if (features.hasDarkModeOn) {
      // Create a style element
      const styleEl = document.createElement('style');

      // Add CSS rules to fix the floating label background in dark mode
      styleEl.innerHTML = `
        .form-floating > .form-control:focus ~ label::after,
        .form-floating > .form-control:not(:placeholder-shown) ~ label::after,
        .form-floating > .form-select ~ label::after,
        .form-floating > label::after {
          background-color: transparent !important;
          content: "" !important;
        }
        
        .form-floating > .form-control:focus ~ label,
        .form-floating > .form-control:not(:placeholder-shown) ~ label,
        .form-floating > .form-select ~ label,
        .form-floating > label {
          background-color: transparent !important;
          padding-right: 0 !important;
        }
        
        .form-floating > .form-control:focus ~ label,
        .form-floating > .form-control:not(:placeholder-shown) ~ label {
          opacity: 0.65;
          transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem);
        }
      `;

      // Add the style element to the document head
      document.head.appendChild(styleEl);

      // Clean up function to remove the style element when component unmounts
      return () => {
        document.head.removeChild(styleEl);
      };
    }
  }, [features.hasDarkModeOn]);

  // Add custom CSS for responsive logo
  useEffect(() => {
    if (features.hasLoginLogo) {
      const styleEl = document.createElement('style');
      if (features.has300pxLogo) {
        styleEl.innerHTML = `
          .logo-img {
            max-width: 300px;
          }
        `;
      } else {
        styleEl.innerHTML = `
        @media (max-width: 767.98px) {
          .logo-img {
            width: 100%;
            max-width: 100%;
          }
        }
        @media (min-width: 768px) {
          .logo-img {
            width: auto;
            max-width: none;
          }
        }
      `;
      }
      document.head.appendChild(styleEl);
      return () => {
        document.head.removeChild(styleEl);
      };
    }
  }, [features.hasLoginLogo, features.has300pxLogo]);

  const helperItems = parseHelperItems(customization.helperItems);

  // Render login form
  return (
    <>
      {features.hasBackgroundImage && customization.bgPath ? (
        <img
          src={customization.bgPath}
          alt=""
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -1,
          }}
        />
      ) : (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(to right, ${hex1}, ${hex2})`,
            zIndex: -1,
          }}
        ></div>
      )}
      <Container
        fluid
        style={{
          minHeight: '100vh',
          position: 'relative',
          zIndex: 1,
          paddingTop: '50px',
        }}
      >
        {helperItems.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              zIndex: 1000,
            }}
          >
            <i
              className={`fas fa-question-circle ${
                features.hasDarkModeOn ? 'text-info' : 'text-primary'
              }`}
              onClick={() => setShowHelpModal(true)}
              style={{
                cursor: 'pointer',
                fontSize: '1.5rem',
              }}
              title="Help"
            ></i>
          </div>
        )}
        <Row>
          <Toaster />
          <Col
            className="text-center"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingTop: '50px',
            }}
          >
            {features.hasLoginLogo && (
              <img
                src={customization.logoPath}
                alt="Logo"
                className="img-fluid logo-img"
              />
            )}
            {features.hasLoginSubtitle && (
              <h2>
                {features.hasLightSubtitle ? (
                  <span style={{ color: 'white' }}>{customization.subtitle}</span>
                ) : (
                  customization.subtitle
                )}
              </h2>
            )}

            <div
              className={`container-fluid mx-auto p-4 ${
                !features.hasDarkModeOn ? 'bg-white' : 'bg-dark'
              } rounded-3 shadow w-100`}
              style={{ maxWidth: '400px' }}
            >
              <div
                className={`h1 text-start ${
                  features.hasDarkModeOn ? 'text-white' : ''
                } `}
              >
                {features.hasRegistration
                  ? isRegistering
                    ? 'Register'
                    : 'Sign in'
                  : 'Sign in'}
              </div>
              <div
                className={`p text-start mb-3 ${
                  features.hasDarkModeOn ? 'text-white' : ''
                }`}
              >
                {customization.accessMessage || 'Enter your info below to access '}
              </div>
              <form onSubmit={handleLogin}>
                {/* Username input */}
                {(!features.hasRegistration || isRegistering) &&
                  (features.hasFirstLastNames ? (
                    <>
                      <div
                        className={`form-floating mb-3 ${
                          features.hasDarkModeOn ? '' : ''
                        }`}
                      >
                        <input
                          id="firstName"
                          className={`form-control ${
                            errors.firstName ? 'is-invalid' : ''
                          } ${
                            features.hasDarkModeOn
                              ? 'text-white border-secondary'
                              : ''
                          }`}
                          style={
                            features.hasDarkModeOn
                              ? { backgroundColor: '#343a40' }
                              : {}
                          }
                          type="text"
                          placeholder="firstName"
                          value={firstName}
                          ref={firstNameInputRef}
                          onChange={handleFirstNameChange}
                        />
                        <label
                          htmlFor="firstName"
                          className={features.hasDarkModeOn ? 'text-light' : ''}
                          style={
                            features.hasDarkModeOn
                              ? {
                                  backgroundColor: 'transparent',
                                  color: 'rgba(255, 255, 255, 0.75)',
                                  paddingLeft: '0.75rem',
                                  paddingRight: '0',
                                  zIndex: '0',
                                }
                              : {}
                          }
                        >
                          First Name
                        </label>
                      </div>
                      <div className="form-floating mb-3">
                        <input
                          id="lastName"
                          className={`form-control ${
                            errors.lastName ? 'is-invalid' : ''
                          } ${
                            features.hasDarkModeOn
                              ? 'text-white border-secondary'
                              : ''
                          }`}
                          style={
                            features.hasDarkModeOn
                              ? { backgroundColor: '#343a40' }
                              : {}
                          }
                          type="text"
                          placeholder="lastName"
                          value={lastName}
                          ref={lastNameInputRef}
                          onChange={handleLastNameChange}
                        />
                        <label
                          htmlFor="lastName"
                          className={features.hasDarkModeOn ? 'text-light' : ''}
                          style={
                            features.hasDarkModeOn
                              ? { backgroundColor: 'transparent' }
                              : {}
                          }
                        >
                          Last Name
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="form-floating mb-3">
                      <input
                        id="userName"
                        className={`form-control ${
                          errors.userName ? 'is-invalid' : ''
                        } ${
                          features.hasDarkModeOn
                            ? 'text-white border-secondary'
                            : ''
                        }`}
                        style={
                          features.hasDarkModeOn
                            ? { backgroundColor: '#343a40' }
                            : {}
                        }
                        type="text"
                        placeholder="userName"
                        value={userName}
                        ref={userNameInputRef}
                        onChange={handleUserNameChange}
                      />
                      <label
                        htmlFor="userName"
                        className={features.hasDarkModeOn ? 'text-light' : ''}
                        style={
                          features.hasDarkModeOn
                            ? { backgroundColor: 'transparent' }
                            : {}
                        }
                      >
                        Name
                      </label>
                    </div>
                  ))}
                {(features.hasRegistration || features.hasEmailField) && (
                  <div className="form-floating mb-3">
                    <input
                      id="email"
                      className={`form-control ${
                        errors.email ? 'is-invalid' : ''
                      } ${
                        features.hasDarkModeOn
                          ? 'text-white border-secondary'
                          : ''
                      }`}
                      style={
                        features.hasDarkModeOn
                          ? { backgroundColor: '#343a40' }
                          : {}
                      }
                      type="email"
                      placeholder="email"
                      value={email}
                      ref={emailInputRef}
                      onChange={handleEmailChange}
                    />
                    <label
                      htmlFor="email"
                      className={features.hasDarkModeOn ? 'text-light' : ''}
                      style={
                        features.hasDarkModeOn
                          ? { backgroundColor: 'transparent' }
                          : {}
                      }
                    >
                      Email
                    </label>
                    {errors.email && (
                      <div className="invalid-feedback">
                        Please enter a valid email address
                      </div>
                    )}
                  </div>
                )}
                {features.hasLocationField &&
                  (!features.hasRegistration || isRegistering) && (
                    <div className="form-floating mb-3">
                      <input
                        id="location"
                        className={`form-control ${
                          errors.location ? 'is-invalid' : ''
                        } ${
                          features.hasDarkModeOn
                            ? 'text-white border-secondary'
                            : ''
                        }`}
                        style={
                          features.hasDarkModeOn
                            ? { backgroundColor: '#343a40' }
                            : {}
                        }
                        type="text"
                        placeholder="location"
                        value={location}
                        ref={locationInputRef}
                        onChange={handleLocationChange}
                      />
                      <label
                        htmlFor="location"
                        className={features.hasDarkModeOn ? 'text-light' : ''}
                        style={
                          features.hasDarkModeOn
                            ? { backgroundColor: 'transparent' }
                            : {}
                        }
                      >
                        Location
                      </label>
                    </div>
                  )}
                {/* Passcode field for registration (if hasRegistrationCode is ON) 
                    OR regular login flow (if hasEventPasscode is ON and hasRegistration is OFF) */}
                {((features.hasRegistrationCode && isRegistering) ||
                  (!features.hasRegistration && features.hasEventPasscode)) && (
                  <div className="form-floating mb-3">
                    <input
                      id="passcode"
                      className={`form-control ${
                        errors.passcode ? 'is-invalid' : ''
                      } ${
                        features.hasDarkModeOn
                          ? 'text-white border-secondary'
                          : ''
                      }`}
                      style={
                        features.hasDarkModeOn
                          ? { backgroundColor: '#343a40' }
                          : {}
                      }
                      type="password"
                      placeholder={
                        isRegistering ? 'Registration Code' : 'Passcode'
                      }
                      value={passcode}
                      ref={passcodeInputRef}
                      onChange={handlePasscodeChange}
                    />
                    <label
                      htmlFor="passcode"
                      className={features.hasDarkModeOn ? 'text-light' : ''}
                      style={
                        features.hasDarkModeOn
                          ? { backgroundColor: 'transparent' }
                          : {}
                      }
                    >
                      {isRegistering ? 'Registration Code' : 'Passcode'}
                    </label>
                  </div>
                )}
                {/* Submit button */}
                <div className="d-grid gap-2">
                  <button className="btn btn-primary py-3" type="submit">
                    {features.hasRegistration
                      ? isRegistering
                        ? 'Register'
                        : 'Sign In'
                      : customization.loginButtonMessage}
                  </button>
                </div>
                {/* <button type="submit" className="btn btn-primary"></button> */}
              </form>
              {features.hasRegistration &&
                (!features.hasPreRegistration || isRegistering) && (
                  <div className="mt-3 text-center">
                    <button
                    className={`btn btn-link ${
                      features.hasDarkModeOn ? 'text-light' : ''
                    }`}
                    style={{ textDecoration: 'none' }}
                    onClick={() => {
                      setIsRegistering(!isRegistering);
                      clearError();
                      setErrors({
                        userName: false,
                        passcode: false,
                        email: false,
                      });
                    }}
                  >
                    {isRegistering
                      ? 'Already have an account? Sign in'
                      : 'Need an account? Register'}
                  </button>
                </div>
              )}
            </div>

            {features.hasLoginLowerImage && customization.lowerImagePath && (
              <img
                src={customization.lowerImagePath}
                alt="Lower Login"
                className="img-fluid mt-4"
                style={{ maxWidth: '400px' }}
              />
            )}

            {features.hasCookieNotification && (
              <div
                className="alert text-center cookiealert show"
                role="alert"
                style={{
                  position: 'fixed',
                  bottom: showCookieNotification ? 0 : '-200px', // Start below viewport when hidden
                  left: 0,
                  right: 0,
                  backgroundColor: '#f3e000',
                  margin: 0,
                  padding: '1rem',
                  borderTop: '1px solid #ffeeba',
                  zIndex: 1000,
                  transition: 'bottom 0.5s ease-in-out', // Add smooth transition
                  opacity: showCookieNotification ? 1 : 0, // Fade in/out
                  transitionProperty: 'bottom, opacity', // Transition both properties
                }}
              >
                Please accept all cookies to ensure you get the best experience
                on this platform.
                <button
                  type="button"
                  className="btn btn-primary acceptcookies ms-3"
                  onClick={handleAcceptCookies}
                >
                  ACCEPT
                </button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
      <HelpModal
        show={showHelpModal}
        onHide={() => setShowHelpModal(false)}
        helperItems={helperItems}
      />
    </>
  );
};

export default LoginComponentPublic;
